import type { IDisposable } from "@codesandbox/pitcher-client";
import { debounce } from "lodash";
import type { Selection, editor } from "monaco-editor";
import { useEffect, useState } from "react";
import type { EditorInput } from "vscode/services";
import { IEditorService, StandaloneServices } from "vscode/services";
import type {
  ICodeEditor,
  IDiffEditor,
} from "vscode/vscode/vs/editor/browser/editorBrowser";
import type { IEditor } from "vscode/vscode/vs/editor/common/editorCommon";

import { useVSCodeExtensionInitializedEffect } from "hooks/useVSCodeExtensionInitializedEffect";

export type EditorType = IEditor | IDiffEditor;

export function isDiffEditor(
  editor: IEditor | IDiffEditor,
): editor is IDiffEditor {
  return editor.getEditorType() === "vs.editor.IDiffEditor";
}

export function isCodeEditor(
  editor: IEditor | IDiffEditor,
): editor is editor.ICodeEditor {
  return editor.getEditorType() === "vs.editor.ICodeEditor";
}

export function useActiveTextEditor(): EditorType | null {
  const [editor, setEditor] = useState<EditorType | null>(null);

  useVSCodeExtensionInitializedEffect(() => {
    const editorService = StandaloneServices.get(IEditorService);
    const activeEditor = editorService.activeTextEditorControl;

    if (activeEditor) {
      setEditor(activeEditor);
    }

    const disposable = editorService.onDidActiveEditorChange(() => {
      setEditor(editorService.activeTextEditorControl ?? null);
    });

    return () => {
      disposable.dispose();
    };
  }, []);

  return editor;
}

/**
 * Will get the active _code editor_, if it's a diff editor, it will return the modified editor
 */
export function useActiveCodeEditor(): ICodeEditor | null {
  const editor = useActiveTextEditor();
  if (!editor) {
    return null;
  }

  if (isDiffEditor(editor)) {
    return editor.getModifiedEditor();
  }

  return isCodeEditor(editor) ? editor : null;
}

export function useActiveCodeEditorSelection(debounceMs = 0):
  | {
      activeCodeEditor: ICodeEditor;
      selection: Selection;
    }
  | {
      activeCodeEditor: null;
      selection: null;
    }
  | {
      activeCodeEditor: ICodeEditor;
      selection: null;
    } {
  const codeEditor = useActiveCodeEditor();
  const [selection, setSelection] = useState<Selection | null>(null);

  useEffect(() => {
    if (!codeEditor) {
      setSelection(null);
      return;
    }

    let handler = (evt: editor.ICursorSelectionChangedEvent) => {
      setSelection(evt.selection);
    };
    if (debounceMs > 0) {
      handler = debounce(handler, debounceMs);
    }

    const disposable = codeEditor.onDidChangeCursorSelection(handler);

    return () => {
      disposable.dispose();
    };
  }, [codeEditor]);

  if (codeEditor) {
    return { activeCodeEditor: codeEditor, selection };
  }

  return { activeCodeEditor: null, selection: null };
}

export function useActiveEditorInput(): EditorInput | null {
  const [editor, setEditor] = useState<EditorInput | null>(null);

  useVSCodeExtensionInitializedEffect(() => {
    let disposable: IDisposable | null = null;
    let disposed = false;

    if (disposed) {
      return;
    }

    const editorService = StandaloneServices.get(IEditorService);
    const activeEditor = editorService.activeEditor;

    if (activeEditor) {
      setEditor(activeEditor);
    }

    disposable = editorService.onDidActiveEditorChange(() => {
      setEditor(editorService.activeEditor ?? null);
    });

    return () => {
      disposed = true;
      if (disposable) {
        disposable.dispose();
      }
    };
  }, []);

  return editor;
}

export function useVisibleEditors() {
  const [editors, setEditors] = useState<readonly EditorType[]>([]);

  useVSCodeExtensionInitializedEffect(() => {
    const editorService = StandaloneServices.get(IEditorService);
    setEditors(editorService.visibleTextEditorControls);

    const disposable = editorService.onDidVisibleEditorsChange(() => {
      setEditors(editorService.visibleTextEditorControls);
    });

    return () => {
      disposable.dispose();
    };
  }, []);

  return editors;
}

export function useTextModelFromEditor(
  editor: EditorType | null,
): editor.IEditorModel | null {
  const [model, setModel] = useState<editor.IEditorModel | null>(
    editor?.getModel() ?? null,
  );

  useEffect(() => {
    if (!editor) {
      setModel(null);
      return;
    }

    const model = editor.getModel();
    setModel(model);
  }, [editor]);

  return model;
}

/**
 * Gets the text model from an editor model, if it's a diff model it will take the modified part
 */
export function getTextModelFromEditorModel(
  model: editor.IEditorModel,
): editor.ITextModel {
  if ("uri" in model) {
    return model;
  }

  if ("modified" in model) {
    return model.modified;
  }

  return model.model.modified;
}
